import * as XLSX from 'xlsx-js-style';
import { Workplace } from '../types';

export interface DocumentInfo {
  code: string;
  title: string;
  short: string;
}

export const STATS_DOCUMENTS: DocumentInfo[] = [
  { code: 'riskAnalysis', title: 'Risk Analizi', short: 'RA' },
  { code: 'emergencyPlan', title: 'Acil Eylem Planı', short: 'AEP' },
  { code: 'trainingPlan', title: 'Yıllık Eğitim Planı', short: 'YEP' },
  { code: 'workPlan', title: 'Yıllık Çalışma Planı', short: 'YÇP' },
  { code: 'instructions', title: 'Talimatlar', short: 'TAL' },
  { code: 'ek2', title: 'EK-2 Sağlık Raporu', short: 'EK2' },
  { code: 'trainings', title: 'Eğitimler', short: 'EĞT' },
  { code: 'employeeRepresentative', title: 'Çalışan Temsilcisi', short: 'ÇT' },
  { code: 'annualEvaluation', title: 'Yıllık Değerlendirme', short: 'YD' },
  { code: 'kkdDelivery', title: 'KKD Teslim', short: 'KKD' },
  { code: 'emergencyDrill', title: 'Acil Durum Tatbikatı', short: 'TAT' },
  { code: 'firstAid', title: 'İlkyardımcı', short: 'İLK' },
  { code: 'machinePeriodicCheck', title: 'Makina Peryodikleri', short: 'MP' },
  { code: 'electricalInstallationCheck', title: 'Elektrik Tesisatı', short: 'ET' },
  { code: 'detectionSuggestionBook', title: 'Tespit Öneri Defteri', short: 'TÖD' },
  { code: 'fieldAuditReport', title: 'Saha Denetim Raporu', short: 'SDR' },
  { code: 'jobTraining', title: 'Kurul Karar Tutanağı', short: 'KKT' }
];

export interface WorkplaceWithDocs {
  workplace: Workplace;
  docs: Record<string, boolean>;
  progress: number;
  readyDocCount: number;
  missingDocCount: number;
}

/**
 * Bir işyerinin veritabanındaki resmi evrak ve ziyaret durumunu okur (localStorage fallback destekli)
 */
export const getWorkplaceDocumentStatus = (workplace: Workplace): WorkplaceWithDocs => {
  const dbDocs = workplace.documents || [];

  // LocalStorage fallback (çevrimdışı/önceki veriler için)
  let localDocsMap: Record<string, any> = {};
  try {
    const saved = localStorage.getItem(`isg_docs_wp_${workplace.id}`);
    if (saved) {
      localDocsMap = JSON.parse(saved);
    }
  } catch (e) {
    console.error(`Storage okuma hatası (${workplace.id}):`, e);
  }

  const docsStatus: Record<string, boolean> = {};
  let readyCount = 0;

  STATS_DOCUMENTS.forEach(doc => {
    // 1. Backend veritabanında bu belge türü var mı?
    const existsInDb = dbDocs.some(d => d.documentType === doc.code);
    // 2. LocalStorage'da var mı?
    const existsInLocal = !!(localDocsMap[doc.code] && (Array.isArray(localDocsMap[doc.code]) ? localDocsMap[doc.code].length > 0 : true));
    
    const exists = existsInDb || existsInLocal;
    docsStatus[doc.code] = exists;
    if (exists) readyCount++;
  });

  // İlerleme hesabı: %50 Ziyaretler + %50 Evraklar
  let visitScore = 0;
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // 1. Veritabanındaki bu aya (cari aya) ait tamamlanmış ziyaret kaydı kontrolü
  const dbVisits = workplace.visits || [];
  const hasCompletedDbVisit = dbVisits.some(v => {
    if (v.status !== 'completed' || !v.visitDate) return false;
    const vDate = new Date(v.visitDate);
    return vDate.getFullYear() === currentYear && (vDate.getMonth() + 1) === currentMonth;
  });

  // 2. LocalStorage veya workplace.monthlyVisits içinde cari ay işaretli mi?
  let hasLocalVisited = false;
  try {
    const savedVisits = localStorage.getItem(`isg_monthly_visits_wp_${workplace.id}`);
    if (savedVisits) {
      const visits: Record<number, boolean> = JSON.parse(savedVisits);
      hasLocalVisited = !!visits[currentMonth];
    }
  } catch {}

  const hasWpMonthlyVisit = !!(workplace.monthlyVisits && (workplace.monthlyVisits[currentMonth] || (workplace.monthlyVisits as any)[String(currentMonth)]));

  if (hasCompletedDbVisit || hasLocalVisited || hasWpMonthlyVisit) {
    visitScore = 50;
  }

  const cancelled = Array.isArray(workplace.cancelledDocuments) ? workplace.cancelledDocuments : [];
  const activeDocs = STATS_DOCUMENTS.filter(d => !cancelled.includes(d.code));
  const activeReadyCount = activeDocs.filter(d => docsStatus[d.code]).length;

  const docScore = activeDocs.length > 0
    ? Math.round((activeReadyCount / activeDocs.length) * 50)
    : 50;
  const progress = Math.min(100, visitScore + docScore);

  return {
    workplace,
    docs: docsStatus,
    progress,
    readyDocCount: readyCount,
    missingDocCount: Math.max(0, activeDocs.length - activeReadyCount)
  };
};

/**
 * İSG Destek PWA formatında dikey başlıklı, renkli resmi Excel çıktısı üretir
 */
export const exportWorkplacesToExcel = (
  items: WorkplaceWithDocs[],
  filenamePrefix: string = 'ISG_Uzman_Istatistik_Raporu'
) => {
  if (!items || items.length === 0) {
    alert('Dışa aktarılacak işyeri kaydı bulunamadı.');
    return;
  }

  const sortedItems = [...items].sort((a, b) =>
    (a.workplace.title || '').localeCompare(b.workplace.title || '', 'tr')
  );

  // Başlıklar: Temel Bilgiler + Resmi Belgeler
  const baseHeaders = [
    'İşyeri Unvanı',
    'SGK Sicil / DETSİS No',
    'Tehlike Sınıfı',
    'Çalışan Sayısı',
    'İl / İlçe',
    'Yetkili Kişi',
    'Telefon',
    'İlerleme (%)'
  ];

  const docHeaders = STATS_DOCUMENTS.map(d => d.title);
  const headers = [...baseHeaders, ...docHeaders];

  const getDangerLabel = (dc: string) => {
    switch (dc) {
      case 'veryDangerous': return 'Çok Tehlikeli';
      case 'dangerous': return 'Tehlikeli';
      default: return 'Az Tehlikeli';
    }
  };

  const data = sortedItems.map(item => {
    const wp = item.workplace;
    const baseRow = [
      wp.title || '',
      wp.sgkNo || '-',
      getDangerLabel(wp.dangerClass),
      wp.employeeCount || 1,
      `${wp.city || ''} ${wp.district ? `/ ${wp.district}` : ''}`.trim() || '-',
      wp.contactPerson || '-',
      wp.contactPhone || '-',
      `%${item.progress}`
    ];

    const cancelled = Array.isArray(wp.cancelledDocuments) ? wp.cancelledDocuments : [];
    const docRow = STATS_DOCUMENTS.map(d => {
      if (cancelled.includes(d.code)) return 'MUAF';
      return item.docs[d.code] ? 'VAR' : 'YOK';
    });
    return [...baseRow, ...docRow];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

  const thinBorder = {
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } }
  };

  const baseHeaderCount = baseHeaders.length;

  // Başlık satırı stilleri (Lacivert Zemin, Beyaz Yazı, Belge Başlıkları 90 Derece Dikey)
  for (let C = 0; C < headers.length; ++C) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!worksheet[cellAddress]) continue;

    const isDocColumn = C >= baseHeaderCount;
    worksheet[cellAddress].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '4F81BD' } }, // İSG Destek Kurumsal Lacivert
      alignment: {
        horizontal: 'center',
        vertical: 'center',
        textRotation: isDocColumn ? 90 : 0
      },
      border: thinBorder
    };
  }

  // Veri hücreleri stilleri (VAR: Yeşil Kalın, YOK: Kırmızı Kalın, MUAF: Gri)
  for (let R = 1; R <= data.length; ++R) {
    const rowItem = sortedItems[R - 1];
    for (let C = 0; C < headers.length; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;

      const val = worksheet[cellAddress].v;
      const s: any = {
        border: thinBorder,
        alignment: { vertical: 'center', sz: 9 }
      };

      if (C === 0) {
        s.alignment.horizontal = 'left';
        s.font = { bold: true, color: { rgb: '0F172A' } };
      } else if (C >= baseHeaderCount) {
        // Belge Kolonu
        s.alignment.horizontal = 'center';
        if (val === 'VAR') {
          s.font = { bold: true, color: { rgb: '00B050' }, sz: 10 }; // Yeşil
          s.fill = { fgColor: { rgb: 'F0FDF4' } };
        } else if (val === 'MUAF') {
          s.font = { bold: true, color: { rgb: '64748B' }, sz: 9 }; // Gri
          s.fill = { fgColor: { rgb: 'F1F5F9' } };
        } else {
          s.font = { bold: true, color: { rgb: 'DC2626' }, sz: 10 }; // Kırmızı
          s.fill = { fgColor: { rgb: 'FEF2F2' } };
        }
      } else {
        s.alignment.horizontal = 'center';
        if (C === 2) {
          // Tehlike Sınıfı Renklendirmesi
          if (rowItem.workplace.dangerClass === 'veryDangerous') {
            s.font = { bold: true, color: { rgb: 'B91C1C' } };
          } else if (rowItem.workplace.dangerClass === 'dangerous') {
            s.font = { bold: true, color: { rgb: 'CA8A04' } };
          } else {
            s.font = { bold: true, color: { rgb: '0F766E' } };
          }
        }
      }

      worksheet[cellAddress].s = s;
    }
  }

  // Kolon Genişlikleri
  worksheet['!cols'] = [
    { wch: 32 }, // İşyeri Unvanı
    { wch: 22 }, // SGK No
    { wch: 15 }, // Tehlike Sınıfı
    { wch: 12 }, // Çalışan Sayısı
    { wch: 18 }, // İl / İlçe
    { wch: 18 }, // Yetkili
    { wch: 15 }, // Telefon
    { wch: 12 }, // İlerleme
    ...Array(docHeaders.length).fill({ wch: 5 }) // Dikey Belge Kolonları
  ];

  // Başlık satırı yüksekliği (Dikey metinler rahat okunsun: 110pt)
  worksheet['!rows'] = [{ hpt: 110 }];

  // Yazdırma Sayfa Yapısı: Yatay (Landscape)
  worksheet['!pageSetup'] = {
    orientation: 'landscape',
    fitToWidth: 1,
    fitToHeight: 999,
    paperSize: 9
  };
  worksheet['!margins'] = { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };
  worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${data.length + 1}` };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Istatistikler');

  const dateStr = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${filenamePrefix}_${dateStr}.xlsx`);
};

/**
 * Seçili aya ait ziyaret planını şık ve renkli bir Excel tablosu olarak dışa aktarır.
 */
export const exportMonthlyPlanningToExcel = (
  monthLabel: string,
  visits: Array<any>,
  filenamePrefix = 'ISG_Aylik_Ziyaret_Plani'
) => {
  const thinBorder = {
    top: { style: 'thin', color: { rgb: '94A3B8' } },
    bottom: { style: 'thin', color: { rgb: '94A3B8' } },
    left: { style: 'thin', color: { rgb: '94A3B8' } },
    right: { style: 'thin', color: { rgb: '94A3B8' } }
  };

  const headerBorder = {
    top: { style: 'medium', color: { rgb: '1E3C72' } },
    bottom: { style: 'medium', color: { rgb: '1E3C72' } },
    left: { style: 'thin', color: { rgb: 'CBD5E1' } },
    right: { style: 'thin', color: { rgb: 'CBD5E1' } }
  };

  // 1. Ziyaretleri tarihe ve saate göre sırala
  const sortedVisits = [...visits].sort((a, b) => {
    const dComp = (a.visitDate || '').localeCompare(b.visitDate || '');
    if (dComp !== 0) return dComp;
    return (a.startTime || '').localeCompare(b.startTime || '');
  });

  // 2. Tablo Başlıkları (Yetkili ve Telefon kaldırıldı, tek A4 sayfaya tam sığar)
  const headers = [
    'Sıra',
    'Ziyaret Tarihi',
    'Gün',
    'Saat Aralığı',
    'İşyeri Unvanı',
    'Tehlike Sınıfı',
    'SGK Sicil No',
    'İl / İlçe',
    'Ziyaret Türü',
    'Durum',
    'Saha Notu'
  ];

  const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

  // 3. Veri Satırları Hazırla
  const rows = sortedVisits.map((v, idx) => {
    let dayName = '';
    if (v.visitDate) {
      const d = new Date(v.visitDate);
      if (!isNaN(d.getTime())) {
        dayName = dayNames[d.getDay()] || '';
      }
    }

    let dangerText = 'Az Tehlikeli';
    const rawDanger = (v.workplace?.dangerClass || '').toLowerCase();
    if (rawDanger.includes('çok') || rawDanger.includes('cok') || rawDanger.includes('very')) {
      dangerText = 'Çok Tehlikeli';
    } else if (rawDanger.includes('tehlike') || rawDanger.includes('dangerous')) {
      dangerText = 'Tehlikeli';
    }

    const isPlanned = v.status === 'planned';
    const statusText = isPlanned ? 'PLANLANDI' : 'TAMAMLANDI';
    const sgkVal = v.workplace?.sgkNo || v.workplace?.sgkSicilNo || '-';

    return [
      idx + 1,
      v.visitDate ? v.visitDate.split('-').reverse().join('.') : '-',
      dayName,
      `${v.startTime || '09:00'} - ${v.endTime || '11:00'}`,
      v.workplace?.title || 'Firma',
      dangerText,
      sgkVal,
      v.workplace?.city ? `${v.workplace.city}${v.workplace.district ? ' / ' + v.workplace.district : ''}` : '-',
      v.visitType || 'Rutin Ziyaret',
      statusText,
      v.notes || '-'
    ];
  });

  // Başlıklar ve Veriler (Row 0: Genel Başlık, Row 1: Boşluk/Alt Başlık, Row 2: Tablo Başlıkları)
  const titleRow = [`${monthLabel.toUpperCase()} - İŞ SAĞLIĞI VE GÜVENLİĞİ SAHA ZİYARET PROGRAMI`];
  const subTitleRow = [`Rapor Oluşturma Tarihi: ${new Date().toLocaleDateString('tr-TR')} | Toplam Planlı Ziyaret: ${visits.length}`];

  const fullData = [
    titleRow,
    subTitleRow,
    headers,
    ...rows
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(fullData);

  // Hücre Birleştirmeleri (Ana Başlık & Alt Başlık tüm sütunlara yayılsın)
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } }
  ];

  // Ana Başlık Stili (Satır 0)
  const titleCellAddr = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (worksheet[titleCellAddr]) {
    worksheet[titleCellAddr].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 13 },
      fill: { fgColor: { rgb: '1E3C72' } },
      alignment: { horizontal: 'center', vertical: 'center' }
    };
  }

  // Alt Başlık Stili (Satır 1)
  const subTitleCellAddr = XLSX.utils.encode_cell({ r: 1, c: 0 });
  if (worksheet[subTitleCellAddr]) {
    worksheet[subTitleCellAddr].s = {
      font: { italic: true, color: { rgb: '334155' }, sz: 9.5 },
      fill: { fgColor: { rgb: 'F1F5F9' } },
      alignment: { horizontal: 'center', vertical: 'center' }
    };
  }

  // Sütun Başlıkları Stili (Satır 2)
  for (let c = 0; c < headers.length; c++) {
    const cellAddr = XLSX.utils.encode_cell({ r: 2, c });
    if (worksheet[cellAddr]) {
      worksheet[cellAddr].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
        fill: { fgColor: { rgb: '2A5298' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: headerBorder
      };
    }
  }

  // Veri Satırları Stilleri (Satır 3'ten itibaren)
  for (let r = 0; r < rows.length; r++) {
    const rowIdx = r + 3;
    const isCompleted = rows[r][9] === 'TAMAMLANDI';
    const bgZebra = r % 2 === 1 ? 'F8FAFC' : 'FFFFFF';

    for (let c = 0; c < headers.length; c++) {
      const cellAddr = XLSX.utils.encode_cell({ r: rowIdx, c });
      if (!worksheet[cellAddr]) continue;

      // c === 6 (SGK Sicil No): wrapText OLMASIN (tek satır kalsın)
      // Diğer metin kolonlarında uzun gelirse METNİ KAYDIR (wrapText: true)
      const shouldWrap = c !== 6 && c !== 0 && c !== 1 && c !== 2 && c !== 3;

      const cellStyle: any = {
        border: thinBorder,
        fill: { fgColor: { rgb: bgZebra } },
        alignment: {
          vertical: 'center',
          sz: 9,
          wrapText: shouldWrap
        }
      };

      // Hizalamalar & Renkler
      if (c === 0 || c === 1 || c === 2 || c === 3 || c === 6) {
        // Sıra, Tarih, Gün, Saat, SGK
        cellStyle.alignment.horizontal = 'center';
      } else if (c === 4) {
        // İşyeri Adı (Metin kaydırmalı)
        cellStyle.alignment.horizontal = 'left';
        cellStyle.font = { bold: true, color: { rgb: '0F172A' }, sz: 9 };
      } else if (c === 5) {
        // Tehlike Sınıfı
        cellStyle.alignment.horizontal = 'center';
        const dangerVal = rows[r][5];
        if (dangerVal === 'Çok Tehlikeli') {
          cellStyle.font = { bold: true, color: { rgb: 'B91C1C' } };
        } else if (dangerVal === 'Tehlikeli') {
          cellStyle.font = { bold: true, color: { rgb: 'B45309' } };
        } else {
          cellStyle.font = { bold: true, color: { rgb: '047857' } };
        }
      } else if (c === 9) {
        // Durum (Planlandı / Tamamlandı)
        cellStyle.alignment.horizontal = 'center';
        if (isCompleted) {
          cellStyle.font = { bold: true, color: { rgb: '166534' } };
          cellStyle.fill = { fgColor: { rgb: 'DCFCE7' } }; // Açık yeşil
        } else {
          cellStyle.font = { bold: true, color: { rgb: '92400e' } };
          cellStyle.fill = { fgColor: { rgb: 'FEF3C7' } }; // Açık kehribar/sarı
        }
      } else {
        cellStyle.alignment.horizontal = 'left';
      }

      worksheet[cellAddr].s = cellStyle;
    }
  }

  // Kolon Genişlikleri (A4 Yatay sayfaya rahatça sığması için daraltıldı, SGK No 22ch tek satır)
  worksheet['!cols'] = [
    { wch: 4 },  // 0: Sıra
    { wch: 10 }, // 1: Tarih
    { wch: 9 },  // 2: Gün
    { wch: 11 }, // 3: Saat Aralığı
    { wch: 14 }, // 4: İşyeri Unvanı (Daha da daraltıldı, metin alt satıra kayar)
    { wch: 11 }, // 5: Tehlike Sınıfı (Metin kayar)
    { wch: 22 }, // 6: SGK Sicil No (Tek satırda kalması için geniş tutuldu)
    { wch: 11 }, // 7: İl / İlçe
    { wch: 12 }, // 8: Ziyaret Türü
    { wch: 11 }, // 9: Durum
    { wch: 13 }  // 10: Saha Notu (Daraltıldı ve metin kayar)
  ];

  // Satır Yükseklikleri (Kaydırılan metinler için yeterli dikey alan)
  const rowHeights = [{ hpt: 26 }, { hpt: 18 }, { hpt: 24 }];
  for (let i = 0; i < rows.length; i++) {
    rowHeights.push({ hpt: 30 });
  }
  worksheet['!rows'] = rowHeights;

  // Sayfa Yapısı: YATAY (Landscape) ve GENİŞLİKTE TAM 1 SAYFAYA KİLİTLEME
  worksheet['!pageSetup'] = {
    orientation: 'landscape',
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9
  };

  // Excel açıldığında Sayfa Düzeni (Page Layout) / FitToPage modunu zorunlu kıl
  worksheet['!sheetViews'] = [
    {
      showGridLines: true
    }
  ];

  // Kenar boşlukları (Dar Margin: 0.2 inç)
  worksheet['!margins'] = {
    left: 0.2,
    right: 0.2,
    top: 0.3,
    bottom: 0.3,
    header: 0.1,
    footer: 0.1
  };

  worksheet['!autofilter'] = { ref: `A3:${XLSX.utils.encode_col(headers.length - 1)}${rows.length + 3}` };

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Aylik_Plan');

  // Worksheet özelliklerine fitToPage ve yatay mod flag'lerini ata
  if (!worksheet['!type']) worksheet['!type'] = 'sheet';
  (worksheet as any)['!pageSetup'] = {
    orientation: 'landscape',
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9
  };

  const safeMonth = monthLabel.replace(/[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ]/g, '_');
  XLSX.writeFile(workbook, `${filenamePrefix}_${safeMonth}.xlsx`);
};
